import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { lockIcon } from './common';

interface MCQProps {
  question?: string;
  options: string[];
  correctAnswerIndex: number;
  onSubmit?: (selectedAnswer: string, isCorrect: boolean) => void;
  disabled?: boolean;
  showLockIcon?: boolean;
  selectedAnswer?: string;
  isAnswered?: boolean;
}

const MCQQuestion: React.FC<MCQProps> = ({
  question,
  options,
  correctAnswerIndex,
  onSubmit,
  disabled,
  showLockIcon,
  selectedAnswer,
  isAnswered
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (selectedAnswer) {
      const index = options.findIndex((o) => o === selectedAnswer);
      if (index >= 0) {
        setSelectedIndex(index);
        setSubmitted(isAnswered || false);
      }
    }
  }, [selectedAnswer, isAnswered, options]);

  const handleOptionClick = (index: number) => {
    if (disabled || submitted) return;

    const selectedOption = options[index];
    const isCorrect = index === correctAnswerIndex;

    setSelectedIndex(index);
    setSubmitted(true);
    onSubmit?.(selectedOption, isCorrect);
  };

  const getState = (index: number) => {
    const isSelected = selectedIndex === index;
    const isCorrect = index === correctAnswerIndex;
    const showFeedback = submitted || isAnswered;

    return {
      isSelected,
      isCorrect,
      showFeedback,
      isCorrectSelected: showFeedback && isSelected && isCorrect,
      isWrongSelected: showFeedback && isSelected && !isCorrect,
      isCorrectNotSelected: showFeedback && isCorrect && !isSelected
    };
  };

  // Figma Colors
  const COLORS = {
    defaultBg: '#1C1C1C',
    defaultBorder: '#444444',
    dimIcon: '#868686',
    correctBg: 'rgba(0,150,45,0.20)',
    wrongBg: 'rgba(255,0,0,0.20)',
    correctBorder: '#868686',
    wrongBorder: '#868686',
    textDefault: '#B6B6B6',
    textHighlight: '#FDFDFD'
  };

  // Circle Icon Components
  const DefaultCircle = (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: `2px solid ${COLORS.dimIcon}`
      }}
    />
  );

  const CorrectCircle = (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: `2px solid ${COLORS.dimIcon}`
      }}
    />
  );

  const WrongCircle = (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '50%',
        border: `2px solid ${COLORS.textHighlight}`,
        position: 'relative'
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: COLORS.textHighlight,
          position: 'absolute',
          left: 4,
          top: 4,
          border: `2px solid ${COLORS.textHighlight}`
        }}
      />
    </Box>
  );

  return (
    <>
      {/* Title */}
      {question && (
        <Typography
          sx={{
            color: '#FDFDFD',
            fontSize: '18px',
            fontWeight: 600,
            fontFamily: 'Open Sans',
            mb: '20px'
          }}
        >
          {question}
        </Typography>
      )}

      <Stack spacing={1.5} width="100%">
        {options.map((option, index) => {
          const {
            isSelected,
            isCorrect,
            showFeedback,
            isCorrectSelected,
            isWrongSelected,
            isCorrectNotSelected
          } = getState(index);

          // Background according to Figma
          let bg = COLORS.defaultBg;
          if (isCorrectSelected || isCorrectNotSelected) bg = COLORS.correctBg;
          if (isWrongSelected) bg = COLORS.wrongBg;

          // Border
          let border = COLORS.defaultBorder;
          if (isCorrectSelected || isCorrectNotSelected) border = COLORS.correctBorder;
          if (isWrongSelected) border = COLORS.wrongBorder;

          // Text Color
          let textColor = COLORS.textDefault;
          if (isCorrectSelected || isCorrectNotSelected) textColor = COLORS.textHighlight;
          if (isWrongSelected) textColor = COLORS.textHighlight;

          return (
            <Box
              key={index}
              onClick={() => handleOptionClick(index)}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px',
                borderRadius: '4px',
                background: bg,
                outline: `1px solid ${border}`,
                outlineOffset: '-1px',
                cursor: disabled || submitted ? 'default' : 'pointer'
              }}
            >
              {/* Left icon */}
              <Box sx={{ width: 20, height: 20 }}>
                {!showFeedback && DefaultCircle}
                {showFeedback && isCorrect && CorrectCircle}
                {showFeedback && isWrongSelected && WrongCircle}
                {showFeedback && isCorrectNotSelected && CorrectCircle && !isCorrect}
              </Box>

              {/* Text */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Open Sans',
                    fontWeight: isCorrectSelected ? 700 : 400,
                    color: textColor
                  }}
                >
                  {option}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </>
  );
};

export default MCQQuestion;
